import { Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { NestedTreeControl } from '@angular/cdk/tree';
import { MatStepper } from '@angular/material/stepper';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { Select } from '@ngxs/store';
import { PerformanceTeamPersonalReview } from 'app/library/performance-personal-review/models/performance-team-personal-review.model';
import { PerformanceProfilePersonalReview } from 'app/library/performance-profile-personal-review/models/performance-profile-personal-review.model';
import { IPerformanceTeamReviewActionPayload } from 'app/library/performance-team-review/interfaces/create-performance-team-review.interface';
import { PerformanceTeamReview } from 'app/library/performance-team-review/models/performance-team-review.model';
import { PerformanceTeamReviewsState } from 'app/library/performance-team-review/models/performance-team-reviews.state';
import { PerformanceTeamReviewService } from 'app/library/performance-team-review/services/performance-team-review.service';
import { IEntityNode } from 'app/projects/shared/src/lib/interfaces/entity-node.interface';
import { BehaviorSubject, Observable } from 'rxjs';
import { NameDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/name/name.component';
import * as moment from 'moment';
import { StepperSelectionEvent } from '@angular/cdk/stepper';

@Component({
    selector: 'app-assign-to-team-dialog',
    templateUrl: './assign-to-team-dialog.component.html',
    styleUrls: ['./assign-to-team-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AssignToTeamDialogComponent implements OnInit {
    @Input()
    listTree: IEntityNode[];

    @Input()
    userId: string;

    @Input()
    title: string;

    @Input()
    performancePersonalReview: PerformanceProfilePersonalReview | PerformanceTeamPersonalReview;

    @Select(PerformanceTeamReviewsState.getFilteredEntities)
    filteredPerformanceTeamReviews$: Observable<PerformanceTeamReview[]>;

    @Select(PerformanceTeamReviewsState.getSearchTerm)
    performanceTeamReviewsStateSearchTerm$: Observable<string[]>;

    @Select(PerformanceTeamReviewsState.getTotalCount)
    performanceTeamReviewsStateTotalCount$: Observable<number>;

    @Select(PerformanceTeamReviewsState.isFetchingList)
    isFetchingPerformanceTeamReviewsList$: Observable<boolean>;

    private _loadPerformanceTeamReviews = new BehaviorSubject<boolean>(null);
    loadPerformanceTeamReviews$ = this._loadPerformanceTeamReviews.asObservable();

    performanceReviews: PerformanceTeamReview[] = [];
    treeControl = new NestedTreeControl<IEntityNode>((node) => node.children);
    dataSource = new MatTreeNestedDataSource<IEntityNode>();
    selectedTeam: IEntityNode;
    selectedPerformanceReview: PerformanceTeamReview;
    performanceReviewDisplayedColumns = ['name', 'type', 'dueDate', 'periodUnderReview', 'status', 'selectAction'];
    isNextBtnLoading: boolean = false;
    isProgrammaticStepChange = false;

    get isLastStep(): boolean {
        return this.stepper?.steps && this?.stepper.selectedIndex === this?.stepper.steps.length - 1;
    }

    private async _fetchReviews(): Promise<void> {
        await this._performanceTeamReviewService.fetchPerformanceTeamReviews(
            this.selectedTeam.id,
            10,
            1,
            '',
            this.performancePersonalReview.type.id,
            this.performancePersonalReview.periodUnderReview.id,
            this.performancePersonalReview.goalSettingPeriod.id,
            this.userId
        );
        this.performanceReviews = this._performanceTeamReviewService.getPerformanceTeamReviews();
    }

    @ViewChild(MatStepper) stepper: MatStepper;

    constructor(public dialogRef: MatDialogRef<AssignToTeamDialogComponent>, private _performanceTeamReviewService: PerformanceTeamReviewService, private _matDialog: MatDialog) {}

    async ngOnInit(): Promise<void> {
        this.dataSource.data = this.listTree;
    }

    hasChild = (_: number, node: IEntityNode) => !!node.children && node.children.length > 0;

    onSelect(node: IEntityNode): void {
        node.isSelected = true;
        this.selectedTeam = node;
    }

    onDeselect(node: IEntityNode): void {
        node.isSelected = false;
        this.selectedTeam = null;
    }

    onClearSelection(): void {
        this.onDeselect(this.selectedTeam);
    }

    onStepChange(event: StepperSelectionEvent, stepper: MatStepper): void {
        if (event.selectedIndex === 1 && !this.isProgrammaticStepChange) {
            this.goForward(stepper, false);
        }

        this.isProgrammaticStepChange = false;
    }

    async goForward(stepper: MatStepper, moveNext: boolean = true): Promise<void> {
        if (this.isNextBtnLoading) return;

        this.isNextBtnLoading = true;
        this.isProgrammaticStepChange = true;

        await this._fetchReviews();

        this.isNextBtnLoading = false;

        if (moveNext) stepper.next();
    }

    goBack(stepper: MatStepper): void {
        stepper.previous();
    }

    onSelectedPerformanceTeamReview(performanceTeamReview: PerformanceTeamReview): void {
        this.selectedPerformanceReview = performanceTeamReview;
    }

    async createPerformanceTeamReview(): Promise<void> {
        const dialogRef = this._matDialog.open(NameDialogComponent, {
            disableClose: false,
            width: '482px',
        });

        dialogRef.componentInstance.dialogTitle = 'PERFORMANCE_REVIEW.PERFORMANCE_REVIEWS.CREATE_NEW_PERFORMANCE_REVIEW';
        dialogRef.componentInstance.placeholder = 'GENERAL.PROPERTY.NAME';

        dialogRef.componentInstance.save.subscribe(async (name: string) => {
            if (name) {
                const profileReview = this.performancePersonalReview as PerformanceProfilePersonalReview;

                const data: IPerformanceTeamReviewActionPayload = {
                    name: name,
                    startDate: moment(profileReview.startDate).format('YYYY-MM-DD'),
                    dueDate: moment(profileReview.dueDate).format('YYYY-MM-DD'),
                    type: {
                        id: profileReview.type.id,
                    },
                    periodUnderReview: {
                        id: profileReview.periodUnderReview.id,
                    },
                    goalSettingPeriod: {
                        id: profileReview.goalSettingPeriod.id,
                    },
                    team: {
                        id: this.selectedTeam.id,
                    },
                    context: 1, // 1 means MOVE_TO_TEAM
                };

                await this._performanceTeamReviewService.createPerformanceTeamReview(data);

                dialogRef.close();
            }
        });
    }

    onSave(): void {
        this.dialogRef.close(this.selectedPerformanceReview);
    }
}
